import {
  Component,
  effect,
  HostListener,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ChatService } from '@fc-core/services/chat.service';
import { AuthService } from '@fc-core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';

import {
  loadCurrentEmployee,
  loadEmployees,
  loadNews,
  retrieveCurrentCustomerAction,
  retrieveCurrentUser,
} from '../../../store';
import { loadFeatures } from '@fc-store/actions/features.actions';
import { PwaService } from '../../services/pwa-service';
import { EventSourceService } from '../../services/event-sources/eventsource.service';
import { PushService } from '@fc-core/platform/push/push.service';
import { FlutterMessagesService } from '@fc-core/services/flutter-messages.service';
import { CamerasEventSourceService } from '@fc-vehicles/ng-cameras/services/cameras-event-source';
import { FilesUploadUiStore } from '@fc-core/files-uploads/store/files-upload-ui.store';
import { FilesUploadsService } from '@fc-core/files-uploads/services/files-uploads.service';
import { ChatTabNotificationService } from '@fc-chats/services/chat-tab-notification.service';

@Component({
  selector: 'fc-root',
  template: `
    <fc-not-supported></fc-not-supported>
    @if (filesUploadUiStore.selectors.showUploads()) {
      <div
        class="fixed left-12 md:left-auto bottom-12 right-12 md:right-24 md:bottom-24 z-50"
      >
        <fc-files-upload></fc-files-upload>
      </div>
    }
    <router-outlet></router-outlet>
    <fc-ota-splash-screen></fc-ota-splash-screen>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  readonly filesUploadUiStore = inject(FilesUploadUiStore);
  store = inject(Store);
  eventSourceService = inject(EventSourceService);
  camerasEventSource = inject(CamerasEventSourceService);
  filesUploadsService = inject(FilesUploadsService);
  flutterMessagesService = inject(FlutterMessagesService);

  constructor(
    public auth: AuthService,
    private router: Router,
    private pwaService: PwaService,
    private chatService: ChatService,
    private pushService: PushService,
  ) {
    inject(ChatTabNotificationService);
    this.pwaService.checkForUpdates();
    this.flutterMessagesService.init();
    effect(() => {
      // if there active uploads show the bottom bar
      if (this.filesUploadsService.hasActiveUploads()) {
        this.filesUploadUiStore.showUploads();
      } else {
        this.filesUploadUiStore.hideUploads();
      }
    });
  }

  ngOnInit() {
    this.chatService.init();
    if (this.router.url.search('auth/login') !== -1) {
      this.auth.refreshToken();
    }
    this.auth.loggedIn$().subscribe((isLogin) => {
      if (isLogin) {
        this.store.dispatch(retrieveCurrentUser());
        this.store.dispatch(retrieveCurrentCustomerAction());
        this.store.dispatch(loadFeatures());
        this.store.dispatch(loadEmployees());
        this.store.dispatch(loadNews());
        this.store.dispatch(loadCurrentEmployee());
        this.pushService.init();
        this.addEmployeeEventSourceListener();
        this.eventSourceService.connectToGlobalEventSource();
        this.camerasEventSource.connectCamerasEvents();
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.filesUploadsService.hasActiveUploads()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  addEmployeeEventSourceListener() {
    const employeeId = this.auth.tokenData?.employee_id;
    if (!employeeId) return;
    this.eventSourceService.connectToEmployeeEventSource(employeeId);
  }
}
